import * as React from 'react';
import { Stage, Layer, Image, Rect, Group, Text } from 'react-konva';
import Konva from 'konva';
import DialogTitle from '@mui/material/DialogTitle';
import Dialog from '@mui/material/Dialog';
import {Typography, TextField, MenuItem, Checkbox, DialogContent, DialogActions, Button} from '@mui/material';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Table from '@mui/material/Table';
import Box from '@mui/material/Box';

import { useTranslation } from "react-i18next";
import { ApiContext } from '../../../App';


const TEMPERATURE = "TEMPERATURE";
const SET_POINT = "SET_POINT";
const HUMIDITY = 'HUMIDITY'
const CO2 = 'CO2'
const VOC = 'VOC'
const PM10 = 'PM10'
const PM25 = 'PM2.5'



function getDistance(p1, p2) {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  }
  
  function getCenter(p1, p2) {
    return {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    };
}


function ShowValueDialog (props) {

    const {open, onClose, data} = props;

    const { t } = useTranslation();


    return (
        <Dialog  open={open} onClose={onClose}>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            {t("Key")}
                        </TableCell>
                        <TableCell>
                            {t(data.key)}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            {t("Value")}
                        </TableCell>
                        <TableCell>
                            {data.value? data.value + "°": "?"}
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    <TableRow>
                        <TableCell>
                            {t("Controller")}
                        </TableCell>
                        <TableCell>
                            {data.controller_name}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            {t("ID")}
                        </TableCell>
                        <TableCell>
                            {data.identifier}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            {t("Location")}
                        </TableCell>
                        <TableCell>
                            {data.location}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            {t("System")}
                        </TableCell>
                        <TableCell>
                            {data.system}
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>
                            {t("Device")}
                        </TableCell>
                        <TableCell>
                            {data.name}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </Dialog>
    )
}

function ChangeSetPointDialog (props) {

    const {open, onClose, data, pointsApi} = props;

    const { t } = useTranslation();

    const HVACCategories = ["SET_POINT", "ON_OFF", "MODE", "FAN_SPEED"];

    const [unmanage, setUnmanage] = React.useState(false);
    const [newValue, setNewValue] = React.useState("OFF");
    const [saveDisabled, setSaveDisabled] = React.useState(false);
    const [keyOptions, setKeyOptions] = React.useState(HVACCategories)
    const [selectedKey, setSelectedKey] = React.useState("SET_POINT")

    const allowedValues = {
        "ON_OFF": ["ON", "OFF"],
        "MODE": ["AUTO", "HEAT", "DRY", "FAN", "COOL"],
        "FAN_SPEED": ["AUTO", "1", "2", "3", "4"]
    }

    const getMiddleSetPoint = (data) => {

        const setPoints = data.set_points?.split(",");
        if (setPoints)
            return setPoints[Math.floor(setPoints.length / 2)]

        return ""
    }

    React.useEffect(() => 
    {
        setNewValue(getMiddleSetPoint(data));

        var hvacOptions = ["SET_POINT"]
        if (data.on_off_identifier)
            hvacOptions.push("ON_OFF")
        if (data.mode_identifier)
            hvacOptions.push("MODE")
        if (data.fan_speed_identifier)
            hvacOptions.push("FAN_SPEED")

        setKeyOptions(data.category === "HVAC"? hvacOptions : ["ON_OFF"]);
    }, 
    [data]
    )

    const handleCloseWithoutSaving = () => {
        onClose()
    }

    const handleChangeKey = (newKey) => {
        setSelectedKey(newKey);
        setNewValue(newKey !== "SET_POINT"? allowedValues[newKey][0] : getMiddleSetPoint(data))
    }

    const handleSave = () => {

        setSaveDisabled(true);

        const onSuccess = () => {
            onClose();
            setSaveDisabled(false);
        }

        const onError = () => {
            window.alert(t("Problem changing value"))
            setSaveDisabled(false);
        }

        pointsApi.changeValue(data.url, selectedKey, newValue, unmanage, onSuccess, onError);
    }


    return (
        <Dialog  open={open} onClose={onClose}>
            <DialogTitle>{t("Change value")}</DialogTitle>
            <DialogContent>
                <Table>
                    <TableBody>
                        <TableRow>
                            <TableCell>
                                {t("ID")}
                            </TableCell>
                            <TableCell>
                                {data.identifier}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                {t("Location")}
                            </TableCell>
                            <TableCell>
                                {data.location}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                {t("System")}
                            </TableCell>
                            <TableCell>
                                {data.system}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                {t("Device")}
                            </TableCell>
                            <TableCell>
                                {data.name}
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                {t("Key")}
                            </TableCell>
                            <TableCell>
                                <TextField
                                    variant="standard"
                                    id="key"
                                    fullWidth
                                    onChange={(event)=>handleChangeKey(event.target.value)}
                                    value={selectedKey}
                                    select
                                    >
                                    {keyOptions.map((option) => (
                                        <MenuItem key={option} value={option}>
                                        {t(option)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>
                                {t("New value")}
                            </TableCell>
                            <TableCell>
                            <TextField
                                variant="standard"
                                id="new_value"
                                fullWidth
                                onChange={(event)=>setNewValue(event.target.value)}
                                value={newValue}
                                select
                                >
                                {selectedKey === "SET_POINT" && data.set_points?.split(",").map((option) => (
                                    <MenuItem key={option} value={option}>
                                    {t(option)}
                                    </MenuItem>
                                ))}
                                {selectedKey !== "SET_POINT" && allowedValues[selectedKey].map((option) => (
                                    <MenuItem key={option} value={option}>
                                    {option}
                                    </MenuItem>
                                ))}
                            </TextField>
                            </TableCell>
                        </TableRow>
                        {
                            data.managed?
                            <TableRow>
                                <TableCell>
                                    {t("Manual")}
                                </TableCell>
                                <TableCell>
                                    <Checkbox 
                                        id="managed"
                                        checked={unmanage} 
                                        onChange={(event)=>setUnmanage(event.target.checked)
                                        }
                                        style={{paddingLeft:0}}
                                    />
                                </TableCell>
                            </TableRow>
                            :
                            null
                        }
                    </TableBody>
                </Table>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCloseWithoutSaving} autoFocus>{t("Cancel")}</Button>
                <Button onClick={handleSave} disabled={saveDisabled}>{t("Save")}</Button>
            </DialogActions>
        </Dialog>
    )
}


export default function FloorPlanViewer (props) {

    const MIN_WIDTH = 600;
    const SCREEN_OVERFLOW_FACTOR_MOBILE = 1.5;

    const {api} = props;

    const { t } = useTranslation();

    const [floorPlanImage, setFloorPlanImage] = React.useState(null);
    const [status, setStatus] = React.useState(props.status);
    const [outputs, setOutputs] = React.useState(props.outputs);
    const [valuePopUpOpen, setValuePopUpOpen] = React.useState(false);
    const [valuePopUpData, setValuePopUpData] = React.useState({});
    const [setPointPopUpOpen, setSetPointPopUpOpen] = React.useState(false);
    const [setPointPopUpData, setSetPointPopUpData] = React.useState({});

    const [stageOriginX, setStageOriginX] = React.useState(0);
    const [stageOriginY, setStageOriginY] = React.useState(0);
    const {floorPlanApi, pointsApi} = React.useContext(ApiContext)

    const stageRef = React.useRef(null);
    let lastCenter = null;
    let lastDist = 0;

    React.useEffect(() => 
    {
        if (props.floorPlanURL) {
            floorPlanApi.retrieve(
                props.floorPlanURL,
                data => {
                    fetch(data.image)
                    .then(response => response.blob())
                    .then(imageBlob => {
                        const imageObjectURL = URL.createObjectURL(imageBlob);                       
                        const image = new window.Image();
                        image.src = imageObjectURL;
                        image.onload = () => {
                            setFloorPlanImage(image);
                        };

                    });
                },
                () => {
                    alert(t("Problem retrieving floor plan"))
                }
            )
            setStageOriginX(0);
            setStageOriginY(0);
        }

        
     }, 
    [props.floorPlanURL]
    )

    React.useEffect(() => 
    {
       setStatus(props.status)
    }, 
    [props.status]
    )

    React.useEffect(() => 
    {
       setOutputs(props.outputs)
    }, 
    [props.outputs]
    )

    React.useEffect(() => 
    {
        setStageOriginX(0);
        setStageOriginY(0);
    }, 
    [props.height, props.width]
    )


    const openValuePopUp = (data) => {
        setValuePopUpOpen(true);
        setValuePopUpData(data);
    }

    const closeValuePopUp = () => {
        setValuePopUpOpen(false)
    }

    const openSetPointPopUp = (data) => {
        setSetPointPopUpOpen(true);
        setSetPointPopUpData(data);
    }

    const closeSetPointPopUp = () => {
        setSetPointPopUpOpen(false)
    }

    const handleDragEnd = (e) => {

        setStatus(
            status.map(
                (item) => item.point === e.target.attrs.id?
                {
                    ...item, floor_plan_x: Math.round(e.target.x()), floor_plan_y: Math.round(e.target.y())
                }
                : item
            )
        )

        if (props.onChange) {

            props.onChange(e.target.attrs.id, Math.round(e.target.x()), Math.round(e.target.y())) 

        }


    }

    var scaleFactor;
    if (floorPlanImage && props.width && props.height) {

        if (props.width >= MIN_WIDTH) {
                scaleFactor = Math.min(props.width/floorPlanImage.width, props.height/floorPlanImage.height);

        } else { // screen smaller than min width
            scaleFactor = props.width*SCREEN_OVERFLOW_FACTOR_MOBILE/floorPlanImage.width;
        }

    }

    function handleTouch(e) {
        e.evt.preventDefault();
        var touch1 = e.evt.touches[0];
        var touch2 = e.evt.touches[1];
        const stage = stageRef.current;
        if (stage !== null) {
          if (touch1 && touch2) {
            stage.stopDrag();
      
            var p1 = {
              x: touch1.clientX,
              y: touch1.clientY
            };
            var p2 = {
              x: touch2.clientX,
              y: touch2.clientY
            };
      
            if (!lastCenter) {
              lastCenter = getCenter(p1, p2);
              return;
            }
            var newCenter = getCenter(p1, p2);
      
            var dist = getDistance(p1, p2);
      
            if (!lastDist) {
              lastDist = dist;
            }
     
            // local coordinates of center point
            var pointTo = {
              x: (newCenter.x - stage.x()) / stage.scaleX(),
              y: (newCenter.y - stage.y()) / stage.scaleX()
            };
      
            var scale = stage.scaleX() * (dist / lastDist);
     
            stage.scaleX(scale);
            stage.scaleY(scale);
      
            // calculate new position of the stage
            var dx = newCenter.x - lastCenter.x;
            var dy = newCenter.y - lastCenter.y;
      
            var newPos = {
              x: newCenter.x - pointTo.x * scale + dx,
              y: newCenter.y - pointTo.y * scale + dy
            };
      
            stage.position(newPos);
            stage.batchDraw();
      
            lastDist = dist;
            lastCenter = newCenter;
          }
        }
      }
      function handleTouchEnd() {
        lastCenter = null;
        lastDist = 0;
        const stage = stageRef.current;
        if (stage !== null) {
            setStageOriginX(stage.x());
            setStageOriginY(stage.y());
        }

      }

    function returnColorFromKey(key) {
        if (key == 'TEMPERATURE') {
            return "#1976d2"
        } else if (key == 'HUMIDITY') {
            return 'green'
        } else if (key == 'CO2') {
            return 'grey'
        } else if (key == 'VOC') {
            return 'orange'
        } else if (key == 'PM10') {
            return 'pink'
        } else if (key == 'PM2.5') {
            return 'purple'
        } else if (key == 'SET_POINT') {
            return '#19d2d2'
        }
    }

    const possibleKeysToIncludeInLegend = [
        SET_POINT,
        HUMIDITY,
        CO2,
        VOC,
        PM10,
        PM25,
        TEMPERATURE, ]

    const keysInStatus = [...new Set(status.map(reading => reading.key))]

    const getUnitFromKey = (key) => {
        if (key == 'TEMPERATURE') {
            return "°"
        } else if (key == 'HUMIDITY') {
            return 'g/m3'
        } else if (key == 'CO2') {
            return 'ppm'
        } else if (key == 'VOC') {
            return 'ppb'
        } else if (key == 'PM10') {
            return 'µg/m³'
        } else if (key == 'PM2.5') {
            return 'µg/m³'
        } 
    }

    const keysInLegend = keysInStatus.filter((key) => possibleKeysToIncludeInLegend.includes(key))

    function getXCoordinate(current_index, l, legendFontSize, boxLength) {
        let xCoordinate = 0
        for (let i=0; i < current_index; i++) {
            xCoordinate += boxLength * 2    
            const textWidth = new Konva.Text({
                text: formatLegendKey(l[i]),
                fontSize: legendFontSize,
                fontFamily: legendFont
            }).width()
            xCoordinate += textWidth
            
        }
        return xCoordinate
    }
    
    const legendFont = 'Roboto, Helvetica, Arial, sans-serif'
    const legendFontSize = 16 / scaleFactor
    const legendBoxWidth = 16 / scaleFactor
    const legendFontColour = 'rgba(0, 0, 0, 0.87)'
    const formatLegendKey = (key) => {
        if (['TEMPERATURE', 'HUMIDITY'].includes(key)) return key.charAt(0).toUpperCase() + key.slice(1).toLowerCase()
        return key
        
    }

    return (
        <>
        <Box id="canvas_container" display="flex" justifyContent="center">

        </Box>
            <Stage x={stageOriginX} y={stageOriginY} id="stage" container="canvas_container"
            width={floorPlanImage? floorPlanImage.width*scaleFactor : 100} 
            height={props.height? props.height : 100} 
            draggable={props.width < MIN_WIDTH}
            scaleX={scaleFactor}
            scaleY={scaleFactor}
            onDragEnd={(e)=>{
                if (e.target.attrs.id === "stage"){
                    setStageOriginX(e.target.x());
                    setStageOriginY(e.target.y());
                }
            }
            }
            ref={stageRef}
            onTouchMove={handleTouch}
            onTouchEnd={handleTouchEnd}
            >
                <Layer>
                    <Image image={floorPlanImage} 
                        height={floorPlanImage?.height * 0.95}
                    />
                    {
                        status.filter((item)=>
                        item.floor_plan_id && 
                        [
                            TEMPERATURE, 
                            SET_POINT,
                            HUMIDITY,
                            CO2,
                            VOC,
                            PM10,
                            PM25].includes(item.key) &&
                            item.display_on_floorplan == 'True' &&
                        props.floorPlanURL.endsWith(item.floor_plan_id + "/")).map(
                            (item) => 
                            <Group 
                                draggable={props.editable}
                                key={item.point} 
                                id={item.point} 
                                x={item.floor_plan_x - 25} 
                                y={item.floor_plan_y - 25}
                                onClick={()=>openValuePopUp(item)}
                                onTap={()=>openValuePopUp(item)}
                                onDragEnd={handleDragEnd}
                                scaleX={1.0/scaleFactor}
                                scaleY={1.0/scaleFactor}
                            >
                                <Rect 
                                    width={55}
                                    height={55}
                                    fill={returnColorFromKey(item.key)} opacity={0.85} 
                                />
                                {
                                    item.key && (item.key === SET_POINT) && <Text width={50} height={10} padding={2}
                                    align="center" verticalAlign="top" text={"Setpoint"} 
                                    fontSize={10}
                                    fill="white"
                                    />
                                }
                                <Text width={55} height={55} 
                                    align="center" verticalAlign="middle" text={item.value? parseFloat(item.value).toFixed(1) + (item.key == 'TEMPERATURE' ? '' : '\n')  + getUnitFromKey(item.key) : "?"} 
                                    fontSize={16}
                                    fill="white"
                                />
                            </Group>
                        )
                    }
                    {
                        outputs.filter((item)=>
                        item.floor_plan &&
                        item.display_on_floorplan == true &&
                        props.floorPlanURL === item.floor_plan 
                        ).map(
                            (item) => 
                            <Group 
                                draggable={props.editable}
                                key={item.url} 
                                id={item.url} 
                                x={item.floor_plan_x - 25} 
                                y={item.floor_plan_y - 25}
                                onClick={()=>openSetPointPopUp(item)}
                                onTap={()=>openSetPointPopUp(item)}
                                onDragEnd={handleDragEnd}
                                scaleX={1.0/scaleFactor}
                                scaleY={1.0/scaleFactor}
                            >
                                <Rect width={13} height={13}  fill="#19d2d2" opacity={0.85} 
                                />
                            </Group>
                        )
                    }
                    {
                        keysInLegend.map((key, index) => 
                            <Group key={index}>
                                <Rect fill={returnColorFromKey(key)} height={legendBoxWidth} width={legendBoxWidth} x={getXCoordinate(index, keysInLegend, legendFontSize, legendFontSize)} y={floorPlanImage?.height * 0.96}/>
                                <Text text={formatLegendKey(key)} fill={legendFontColour} fontSize={legendFontSize} fontFamily={legendFont} x={getXCoordinate(index, keysInLegend, legendFontSize, legendFontSize) + 22/scaleFactor} y={floorPlanImage?.height * 0.96 + 4}/>
                            </Group>
                            )
                    }
                </Layer>
            </Stage>
            <ShowValueDialog open={valuePopUpOpen} onClose={closeValuePopUp} data={valuePopUpData}/>
            <ChangeSetPointDialog open={setPointPopUpOpen} onClose={closeSetPointPopUp} data={setPointPopUpData} pointsApi={pointsApi}/>
        </>
        
      );
}